'use client'
import { useRef, useState } from 'react'
import { useFormik } from 'formik'
import Link from 'next/link'
import Image from 'next/image'
import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import WhatsAppIcon from '@/images/whatsapp.svg'
import XIcon from '@/images/x.svg'
import LinkedInIcon from '@/images/linkedin.svg'
import LinkIcon from '@/images/llink.svg'
import handleRecaptcha from '@/utils/recaptcha'
import { ProductGallery } from '@/components/molecules/ProductGallery'
import Video from '@/components/atoms/Video'
import { Input } from '@/components/atoms/Input'
import { LinkButton } from '@/components/atoms/LinkButton'
import { ProductImage } from '@/interfaces/pages'
import {
  IBudgetRequest,
  IPostBudgetRequest,
  IPostSoldOutRequest,
} from '@/interfaces/actions'
import ErrorLabel from '@/components/atoms/ErrorLabel'
import { postBudgetRequest, postSoldOutRequest } from '@/lib/actions'
import { notify } from '@/utils/notification'
import './styles.scss'

interface ProductDetailsProps {
  products: ProductImage[]
  product: {
    id: string
    available: boolean
    slug: string
    new?: boolean
  }
  videoId?: string
  description: string
}
const ProvideProductDataSchema = Yup.object().shape({
  productManufacturer: Yup.string().optional(),
  productManufacturingYear: Yup.number().optional(),
  productModel: Yup.string().optional(),
  productPrice: Yup.number().optional(),
})

const RequesterDataSchema = Yup.object().shape({
  companyActivityBranch: Yup.string().optional(),
  companyName: Yup.string().required('Campo Obrigatório'),
  email: Yup.string().required('Campo Obrigatório'),
  fullName: Yup.string().required('Campo Obrigatório'),
  phone: Yup.string().required('Campo Obrigatório'),
})

const MainDataSchema = Yup.object().shape({
  provideProductData: ProvideProductDataSchema,
  requesterData: RequesterDataSchema,
  amount: Yup.string().required('Campo Obrigatório'),
})

export const ProductDetails = ({
  product,
  products = [],
  videoId,
  description,
}: ProductDetailsProps) => {
  const [isMachineInfoVisible, setMachineInfoVisible] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const machineInfoRef = useRef(null)
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const toggleMachineInfo = () => {
    if (machineInfoRef.current) {
      const machineInfo: HTMLElement = machineInfoRef.current
      machineInfo.classList.toggle('product-description-collapse-out')
    }
    setMachineInfoVisible(!isMachineInfoVisible)
  }
  const onSubmit = async (data: IBudgetRequest) => {
    if (!recaptchaRef || !recaptchaRef.current) return
    const validRecaptcha = await handleRecaptcha(recaptchaRef)
    if (!validRecaptcha) return
    let response = null
    if (product.available) {
      const body: IPostBudgetRequest['body'] = {
        data: {
          ...data,
          product: product.id,
        },
      }
      response = await postBudgetRequest(body)
    } else {
      const body: IPostSoldOutRequest['body'] = {
        data: {
          requesterData: data.requesterData,
          amount: data.amount,
          product: product.id,
        },
      }
      response = await postSoldOutRequest(body)
    }
    if ('error' in response) {
      notify(
        'Houve um erro na solicitação. Verifique se os campos estão corretos.',
        'error',
      )
    } else {
      notify('Cadastro feito com sucesso!', 'success')
      recaptchaRef.current.reset()
      formik.resetForm()
    }
  }
  const formik = useFormik<IBudgetRequest>({
    initialValues: {
      provideProductData: {
        productManufacturer: '',
        productManufacturingYear: '',
        productModel: '',
        productPrice: '',
      },
      product: '',
      amount: '',
      requesterData: {
        companyActivityBranch: '',
        companyName: '',
        email: '',
        fullName: '',
        phone: '',
      },
    },
    validationSchema: MainDataSchema,
    onSubmit,
    validateOnChange: true,
  })

  const currentUrl = `https://www.camargomaquinasfood.com.br/${product.slug}`

  return (
    <section className="product-details-section">
      <div className="product-details-media">
        {products.length > 0 ? (
          <ProductGallery products={products} isNew={product.new} />
        ) : (
          <div className="no-image">Sem imagem disponível</div>
        )}
        {videoId && (
          <div className="video-wrapper">
            <Video width={560} height={315} videoId={videoId} />
          </div>
        )}
      </div>
      <div className="product-details-data-section">
        <div
          className="product-details-description"
          dangerouslySetInnerHTML={{ __html: description }}
        />
        <div className="product-details-inputs">
          {product.available ? (
            <h3> Peça uma cotação!</h3>
          ) : (
            <>
              <h3>Avise-me quando disponível</h3>
              <p>
                Por favor, preencha o formulário abaixo que lhe avisaremos assim
                que este equipamento estiver disponível.
              </p>
            </>
          )}

          <form id="budgetRequestForm" onSubmit={formik.handleSubmit}>
            <fieldset>
              <Input
                id="requesterData.fullName"
                type="text"
                autoComplete="off"
                label="Nome completo"
                required
                {...formik.getFieldProps('requesterData.fullName')}
              />
              {formik.touched.requesterData?.fullName &&
                formik.errors.requesterData?.fullName && (
                  <ErrorLabel>
                    {formik.errors.requesterData.fullName}
                  </ErrorLabel>
                )}
            </fieldset>
            <fieldset>
              <Input
                id="requesterData.companyName"
                type="text"
                autoComplete="off"
                label="Empresa"
                required
                {...formik.getFieldProps('requesterData.companyName')}
              />
              {formik.touched.requesterData?.companyName &&
                formik.errors.requesterData?.companyName && (
                  <ErrorLabel>
                    {formik.errors.requesterData.companyName}
                  </ErrorLabel>
                )}
            </fieldset>
            <fieldset>
              <Input
                id="requesterData.companyActivityBranch"
                type="text"
                autoComplete="off"
                label="Ramo de atuação"
                {...formik.getFieldProps('requesterData.companyActivityBranch')}
              />

              {formik.touched.requesterData?.companyActivityBranch &&
                formik.errors.requesterData?.companyActivityBranch && (
                  <ErrorLabel>
                    {formik.errors.requesterData.companyActivityBranch}
                  </ErrorLabel>
                )}
            </fieldset>
            <fieldset>
              <Input
                id="requesterData.phone"
                type="text"
                autoComplete="off"
                label="Celular"
                required
                {...formik.getFieldProps('requesterData.phone')}
              />
              {formik.touched.requesterData?.phone &&
                formik.errors.requesterData?.phone && (
                  <ErrorLabel>{formik.errors.requesterData.phone}</ErrorLabel>
                )}
            </fieldset>
            <fieldset>
              <Input
                id="requesterData.email"
                type="email"
                label="E-mail"
                required
                {...formik.getFieldProps('requesterData.email')}
              />
              {formik.touched.requesterData?.email &&
                formik.errors.requesterData?.email && (
                  <ErrorLabel>{formik.errors.requesterData.email}</ErrorLabel>
                )}
            </fieldset>
            <fieldset>
              <Input
                id="amount"
                type="text"
                label="Volume de produção desejado"
                autoComplete="off"
                required
                {...formik.getFieldProps('amount')}
              />
              {formik.touched?.amount && formik.errors?.amount && (
                <ErrorLabel>{formik.errors.amount}</ErrorLabel>
              )}
            </fieldset>
            {product.available && (
              <>
                <div className="checkContainer">
                  <Input
                    id="inputHaveMachine"
                    type="checkbox"
                    label="Você tem máquina à venda?"
                    name="machine_checkbox"
                    value="1"
                    onChange={toggleMachineInfo}
                  />
                </div>

                <div
                  ref={machineInfoRef}
                  className="product-description-collapse-in"
                  id="machineInfoCollapse"
                >
                  <div className="machine-info">
                    <fieldset>
                      <Input
                        id="provideProductData.productManufacturer"
                        type="text"
                        autoComplete="off"
                        label="Marca da máquina"
                        {...formik.getFieldProps(
                          'provideProductData.productManufacturer',
                        )}
                      />
                      {formik.touched.provideProductData?.productManufacturer &&
                        formik.errors.provideProductData
                          ?.productManufacturer && (
                          <ErrorLabel>
                            {
                              formik.errors.provideProductData
                                .productManufacturer
                            }
                          </ErrorLabel>
                        )}
                    </fieldset>
                    <fieldset>
                      <Input
                        id="provideProductData.productModel"
                        type="text"
                        autoComplete="off"
                        label="Modelo da máquina"
                        {...formik.getFieldProps(
                          'provideProductData.productModel',
                        )}
                      />
                      {formik.touched.provideProductData?.productModel &&
                        formik.errors.provideProductData?.productModel && (
                          <ErrorLabel>
                            {formik.errors.provideProductData.productModel}
                          </ErrorLabel>
                        )}
                    </fieldset>
                    <fieldset>
                      <Input
                        id="provideProductData.productManufacturingYear"
                        type="text"
                        autoComplete="off"
                        label="Ano de fabricação"
                        {...formik.getFieldProps(
                          'provideProductData.productManufacturingYear',
                        )}
                      />
                      {formik.touched.provideProductData
                        ?.productManufacturingYear &&
                        formik.errors.provideProductData
                          ?.productManufacturingYear && (
                          <ErrorLabel>
                            {
                              formik.errors.provideProductData
                                .productManufacturingYear
                            }
                          </ErrorLabel>
                        )}
                    </fieldset>
                    <fieldset>
                      <Input
                        id="provideProductData.productPrice"
                        type="text"
                        autoComplete="off"
                        label="Valor da máquina"
                        {...formik.getFieldProps(
                          'provideProductData.productPrice',
                        )}
                      />
                      {formik.touched.provideProductData?.productPrice &&
                        formik.errors.provideProductData?.productPrice && (
                          <ErrorLabel>
                            {formik.errors.provideProductData.productPrice}
                          </ErrorLabel>
                        )}
                    </fieldset>
                  </div>
                </div>
              </>
            )}
            <div className="product-description-recaptcha-section">
              {recaptchaKey && (
                <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} />
              )}
            </div>
            <div className="product-description-button">
              <LinkButton type="submit" label="Enviar" />
            </div>
          </form>
          <div className="border"></div>
        </div>
        <div className="share-buttons">
          <h4>Compartilhe: </h4>
          <Link
            href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(currentUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image src={LinkedInIcon} alt="LinkedIn" />
          </Link>
          <Link
            href={`https://twitter.com/share?url=${encodeURIComponent(currentUrl)}`}
            className="twitter-share-button"
            data-show-count="false"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image src={XIcon} alt="X" />
          </Link>
          <Link
            className="whatsapp"
            target="_blank"
            rel="noreferrer"
            href="https://tintim.link/whatsapp/d67fc36a-3abd-4262-a6ac-a3b65f56c304/43f0cde3-4d2b-4d65-b447-dcc9eea30128"
            aria-label="Entre em contato pelo WhatsApp"
            title="Entre em contato pelo WhatsApp"
          >
            <Image src={WhatsAppIcon} alt="WhatsApp" />
          </Link>

          <button
            aria-label={linkCopied ? 'Link copiado!' : 'Copiar link do produto'}
            title={linkCopied ? 'Link copiado!' : 'Copiar link do produto'}
            onClick={() => {
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard
                  .writeText(currentUrl)
                  .then(() => {
                    setLinkCopied(true)
                    setTimeout(() => setLinkCopied(false), 2000)
                  })
                  .catch(() => {
                    // Fallback silently — clipboard may be unavailable in HTTP/mobile contexts
                  })
              }
            }}
          >
            <Image src={LinkIcon} alt="Link" />
          </button>
        </div>
      </div>
    </section>
  )
}
